// Tribe synergy chip — your position on one tribe's 2/4/6 ladder.
//
// Used in the tavern header. Shows the tribe name, your fielded count, which
// breakpoints are hit, how far the next one is, and whether hand or shop
// could get you there.
//
// Two rules this component must not break:
//
//   1. FIELDED IS NOT POTENTIAL. `count` is what the engine sees. Hand and
//      shop are projections, rendered in a different colour and position so
//      they can never be misread as the real number.
//
//   2. BREAKPOINTS REPLACE. Only the highest rung reached applies
//      (getActiveBreakpoint). The chip shows the ACTIVE rung's description
//      only — the whole ladder, including the rungs you haven't reached,
//      lives in the SynergyPanel drawer.
//
// Self-triggered breakpoint pulse — when count crosses a 2/4/6 threshold
// the chip briefly glows + scales (framer-motion). Reduced-motion users
// get the color/border change but no kinetic emphasis.

import { useEffect, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import type { TribeProgress } from './synergyProgress';
import { orderTribesForRow, startedTribes, capRow } from './synergyProgress';
import { useEffectiveReducedMotion } from './useReducedMotion';
import { EDGES } from '../theme/tokens';

const BREAKPOINTS = [2, 4, 6] as const;

function hexFromColorInt(n: number): string {
  return `#${n.toString(16).padStart(6, '0')}`;
}

interface SynergyChipProps {
  progress: TribeProgress;
  /** Drop the description line — for tight sidebars / mobile HUD. */
  compact?: boolean;
}

export function SynergyChip({ progress, compact = false }: SynergyChipProps) {
  const { tribe, count, fromHand, fromShop, active, next, toNext } = progress;
  const tribeColor = hexFromColorInt(tribe.color);
  const isActive = active !== null;
  const activeBreakpoint = active?.threshold ?? null;
  const reducedMotion = useEffectiveReducedMotion();

  // Self-managed "just hit" emphasis. Fires only when the active breakpoint
  // goes UP — leaving one by selling shouldn't celebrate. Stored in a ref so
  // the first mount doesn't flash on every page load.
  const prevBreakpointRef = useRef<number | null>(activeBreakpoint);
  const [justHit, setJustHit] = useState(false);

  useEffect(() => {
    const prev = prevBreakpointRef.current;
    if (activeBreakpoint !== null && (prev === null || activeBreakpoint > prev)) {
      setJustHit(true);
      const t = setTimeout(() => setJustHit(false), 900);
      return () => clearTimeout(t);
    }
  }, [activeBreakpoint]);

  // Update the ref after the effect so the next comparison uses current
  // as the new baseline.
  useEffect(() => {
    prevBreakpointRef.current = activeBreakpoint;
  });

  const pulseAnim =
    justHit && !reducedMotion
      ? {
          scale: [1, 1.08, 1],
          boxShadow: [
            `0 0 8px ${tribeColor}55`,
            `0 0 24px ${tribeColor}cc`,
            `0 0 8px ${tribeColor}55`,
          ],
        }
      : {};

  return (
    <motion.div
      animate={pulseAnim}
      transition={{ duration: 0.9, ease: 'easeInOut' }}
      className={`relative flex shrink-0 flex-col gap-[2px] rounded-sm border bg-stucco1 px-sm py-[6px] ${
        compact ? 'min-w-[92px]' : 'min-w-[120px]'
      }`}
      style={{
        borderColor: isActive ? `${tribeColor}aa` : EDGES.soft,
        boxShadow: isActive ? `0 0 8px ${tribeColor}55` : undefined,
      }}
      aria-label={
        `${tribe.name} synergy: ${count} fielded` +
        (activeBreakpoint
          ? `, breakpoint ${activeBreakpoint} active`
          : ', no breakpoint yet') +
        (next ? `, ${toNext} more for ${next.threshold}` : ', maxed') +
        (fromHand > 0 ? `, ${fromHand} in hand` : '') +
        (fromShop > 0 ? `, ${fromShop} in shop` : '')
      }
    >
      {/* Tribe name + fielded count. The ghost "+n" is hand potential and is
          deliberately smaller and a different colour to the real count. */}
      <div className="flex items-baseline justify-between gap-xs">
        <span
          className="truncate font-data text-[10px] uppercase tracking-[0.15em] md:text-[11px]"
          style={{ color: isActive ? tribeColor : '#8a7e6c' }}
        >
          {tribe.name}
        </span>
        <span className="flex shrink-0 items-baseline gap-[2px]">
          <span
            className="font-data text-[12px] font-bold leading-none md:text-[14px]"
            style={{ color: isActive ? '#f5ecd6' : '#8a7e6c' }}
          >
            {count}
          </span>
          {fromHand > 0 && (
            <span className="font-data text-[9px] leading-none text-iceCrystal md:text-[10px]">
              +{fromHand}
            </span>
          )}
        </span>
      </div>

      {/* Breakpoint dots */}
      <div className="flex items-center gap-[3px]">
        {BREAKPOINTS.map((bp) => {
          const hit = count >= bp;
          const isCurrent = bp === activeBreakpoint;
          return (
            <div key={bp} className="flex items-center gap-[2px]" aria-hidden>
              <span
                className={`inline-block h-[6px] w-[6px] rounded-full transition-colors ${
                  isCurrent ? 'ring-1 ring-bone/40' : ''
                }`}
                style={{ backgroundColor: hit ? tribeColor : EDGES.mid }}
              />
              <span
                className={`font-data text-[9px] ${
                  hit ? 'text-marker' : 'text-graphite'
                } ${isCurrent ? 'font-bold' : ''}`}
              >
                {bp}
              </span>
            </div>
          );
        })}
        {/* Shop availability is a marker, not a count. The shop rerolls
            constantly; a live number here would make the row churn on every
            refresh. A dot answers "buyable now" and stays stable. */}
        {fromShop > 0 && (
          <span
            className="ml-[2px] font-data text-[9px] leading-none text-sodium"
            aria-hidden
          >
            ◆
          </span>
        )}
      </div>

      {/* Payoff / progress line — gated to non-compact. The tavern's vertical
          budget on a 390px phone is the constraint the whole screen fights;
          a third line on every chip is not affordable there. */}
      {!compact && (
        <p className="mt-[2px] line-clamp-2 text-[10px] leading-snug text-marker md:text-[11px]">
          {active
            ? active.description
            : next
              ? `${toNext} more → ${next.threshold}`
              : ''}
        </p>
      )}
    </motion.div>
  );
}

interface SynergyChipRowProps {
  progress: TribeProgress[];
  compact?: boolean;
  /** Hide tribes below this fielded count. 1 = "anything you've started". */
  minCount?: number;
  /** Cap the row; the remainder surfaces as a "+N" affordance, never
      silently dropped. */
  limit?: number;
  /** Opens the full browser. Makes the whole row one tap target. */
  onOpen?: () => void;
}

export function SynergyChipRow({
  progress,
  compact = false,
  minCount = 1,
  limit,
  onOpen,
}: SynergyChipRowProps) {
  const started = orderTribesForRow(startedTribes(progress, minCount));
  const { shown, overflowCount } = limit
    ? capRow(started, limit)
    : { shown: started, overflowCount: 0 };

  const content =
    started.length === 0 ? (
      <span className="font-data text-[10px] uppercase tracking-[0.2em] text-graphite md:text-[11px]">
        {onOpen ? 'no tribes yet — see all 6' : 'no tribes yet'}
      </span>
    ) : (
      <>
        {shown.map((p) => (
          <SynergyChip key={p.tribe.id} progress={p} compact={compact} />
        ))}
        {overflowCount > 0 && (
          <span className="shrink-0 self-center font-data text-[10px] uppercase tracking-[0.2em] text-graphite md:text-[11px]">
            +{overflowCount}
          </span>
        )}
      </>
    );

  if (!onOpen) {
    return <div className="flex flex-wrap items-center gap-xs md:gap-sm">{content}</div>;
  }

  return (
    <button
      type="button"
      onClick={onOpen}
      aria-label="Open tribe synergies"
      // min-h-[44px]: this is the only route to the synergy browser on a
      // phone, and the chip row alone left it an 18px-tall tap target.
      className="flex min-h-[44px] min-w-0 max-w-full items-center gap-xs overflow-hidden rounded-sm text-left transition-opacity hover:opacity-90 md:gap-sm"
    >
      {content}
      <span
        aria-hidden
        className="shrink-0 self-center font-data text-[12px] text-graphite md:text-[14px]"
      >
        ›
      </span>
    </button>
  );
}
